/**
 * MyRitualityProfileHeader Component
 * Collapsible profile header for the My Rituality dashboard.
 * Shows avatar, name, username, followers/following, bio, socials, tags, and action buttons.
 * Adapted from ProfileInfoSectionDetail for the dashboard context (always own profile, dark theme).
 */

import { ChevronRight, ChevronUp, Instagram, Link2, MapPin } from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  EyeIcon,
  FacebookIcon,
  PencilIcon,
  SettingIcon,
  UploadIcon,
  XIcon,
} from "@/components/icons"
import { RitualityIcon } from "@/components/shared/RitualityIcon"
import { ShareModal } from "@/components/shared/ShareModal"
import { TagChip } from "@/components/shared/TagChip"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useIntentions } from "@/features/profile/hooks/useIntentions"
import type { ProfileStatistics } from "@/features/profile/types/profile.types"
import { useTranslation } from "@/language"
import { cn, extractSocialHandle } from "@/lib"

interface MyRitualityProfileHeaderProps {
  statistics?: ProfileStatistics | null
  onFollowersClick?: () => void
  onFollowingClick?: () => void
}

export function MyRitualityProfileHeader({
  statistics,
  onFollowersClick,
  onFollowingClick,
}: MyRitualityProfileHeaderProps) {
  const { t } = useTranslation("myRituality")
  const navigate = useNavigate()
  const user = useAuthStore(state => state.user)
  const [detailsExpanded, setDetailsExpanded] = useState(true)
  const [shareOpen, setShareOpen] = useState(false)

  const { intentions } = useIntentions(user?.id ?? "")

  if (!user) return null

  /**
   * Check if user has any profile data to display
   * Returns false if profile is empty (should show empty state CTA)
   */
  const hasProfileData = (): boolean => {
    // Check 1: Bio
    if (user.bio?.trim()) return true

    // Check 2: Spiritual traditions
    if (user.spiritual_traditions && user.spiritual_traditions.length > 0) return true

    // Check 3: Profile photo (cover image)
    const hasProfilePic = user.profile_photos?.some(p => p.isCover || p.is_cover_image)
    if (hasProfilePic) return true

    // Check 4: Ethnic backgrounds
    if (user.ethnic_backgrounds && user.ethnic_backgrounds.length > 0) return true

    // Check 5: Gallery photos (non-cover profile photos)
    const hasGalleryPhotos = user.profile_photos?.some(p => !p.isCover && !p.is_cover_image)
    if (hasGalleryPhotos) return true

    // Check 6: Intentions
    if (user.intentions && user.intentions.length > 0) return true

    return false
  }

  const showEmptyState = !hasProfileData()

  const profilePhoto = user.profile_photos?.find(p => p.is_cover_image || p.isCover)?.filePath
  const tags = user.tags?.map(tag => tag.name) || []
  const checkedIntentionTitles = intentions.filter(i => i.is_checked).map(i => i.title)
  const allChips = [...tags, ...checkedIntentionTitles]
  const bio = user.bio || ""

  return (
    <>
      {/* Profile Section */}
      <div className="max-w-360 px-5 md:px-17.5 mx-auto flex flex-col items-center md:flex-row md:items-start gap-5 md:pt-10">
        {/* Avatar */}
        <div className="shrink-0">
          {profilePhoto ? (
            <Avatar
              src={profilePhoto}
              fallback={user.name || ""}
              size="lg"
              className="w-[140px] h-[140px] rounded-full"
            />
          ) : (
            <div className="w-[140px] h-[140px] rounded-full bg-card flex items-center justify-center">
              <RitualityIcon size={48} className="text-green" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0 flex flex-col items-center md:items-start w-full">
          <div className="min-w-0 flex flex-col gap-2 md:flex-row justify-between items-center md:items-start w-full">
            <div className="flex flex-col">
              {/* Name */}
              <h1 className="text-[20px] md:text-[30px] font-semibold leading-[40px] uppercase text-primary text-center md:text-left">
                {user.name}
              </h1>

              {/* Username */}
              {user.username && (
                <p className="text-sm text-primary leading-5.5 md:leading-5 opacity-50 md:mt-1 text-center md:text-left">
                  @{user.username}
                </p>
              )}
            </div>
            {/* Desktop action buttons */}
            <div className="hidden md:flex flex-wrap items-center gap-2.5 shrink-0 mt-1.25">
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2.5 px-3 py-2.5 h-11 border-gradient-3d bg-white/5 rounded-[10px] text-primary"
                onClick={() => navigate(`/profile/${user.id}`)}
              >
                <EyeIcon className="!w-5 !h-5" />
                <span className="text-sm font-semibold">{t("profileHeader.seePublicProfile")}</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2.5 px-3 py-2.5 h-11 border border-gradient-3d bg-white/5 rounded-[10px] text-primary"
                onClick={() => navigate("/profile/edit")}
              >
                <PencilIcon className="!w-4.5 !h-4.5" />
                <span className="text-sm font-semibold">{t("profileHeader.editProfile")}</span>
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-11 w-11 border border-gradient-3d bg-white/5 rounded-[10px] text-primary"
                onClick={() => navigate("/settings")}
                aria-label={t("profileHeader.settings")}
              >
                <SettingIcon className="!w-4.5 !h-4.5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-11 w-11 border border-gradient-3d bg-white/5 rounded-[10px] text-primary"
                onClick={() => setShareOpen(true)}
                aria-label={t("profileHeader.share")}
              >
                <UploadIcon className="!w-4.5 !h-4.5" />
              </Button>
            </div>
          </div>

          {/* Followers / Following */}
          <div className="flex items-center gap-5 mt-2.5s md:mt-5">
            <button
              type="button"
              className="text-sm font-semibold text-primary cursor-pointer hover:opacity-80 transition-opacity"
              onClick={onFollowersClick}
            >
              {statistics?.followersCount ?? 0} {t("profileHeader.followers")}
            </button>
            <button
              type="button"
              className="text-sm font-semibold text-primary cursor-pointer hover:opacity-80 transition-opacity"
              onClick={onFollowingClick}
            >
              {statistics?.followingCount ?? 0} {t("profileHeader.following")}
            </button>
          </div>

          {/* Location - always visible */}
          {(user.city || user.state) && (
            <div className="flex items-center gap-2.5 md:gap-1.5 text-sm leading-5.5 md:leading-5 text-primary mt-2.5">
              <MapPin className="w-5.5 h-5.5 md:w-5 md:h-5 shrink-0" />
              <span>
                {user.city}
                {user.city && user.state && ", "}
                {user.state}
              </span>
            </div>
          )}

          {/* Expanded details */}
          {detailsExpanded && (
            <div className="w-full flex flex-col items-start gap-[23px] mt-5.5 md:mt-5">
              {/* Bio */}
              {bio && (
                <p className="text-sm text-primary leading-relaxed whitespace-pre-wrap text-left w-full">
                  {bio}
                </p>
              )}

              {/* Social links - vertical on mobile, horizontal on desktop */}
              <div className="flex flex-col md:flex-row md:flex-wrap items-start md:items-center gap-2.5 md:gap-6 w-full">
                {user.website_url && (
                  <a
                    href={
                      user.website_url.startsWith("http")
                        ? user.website_url
                        : `https://${user.website_url}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2.5 text-sm text-primary font-semibold hover:opacity-80 transition-opacity"
                  >
                    <Link2 className="w-5 h-5" />
                    <span>{user.website_url}</span>
                  </a>
                )}
                {user.twitter_url && (
                  <a
                    href={
                      user.twitter_url.startsWith("http")
                        ? user.twitter_url
                        : `https://twitter.com/${user.twitter_url}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2.5 text-sm text-primary font-semibold hover:opacity-80 transition-opacity"
                  >
                    <XIcon className="w-5 h-5" />
                    <span>{extractSocialHandle(user.twitter_url)}</span>
                  </a>
                )}
                {user.instagram_url && (
                  <a
                    href={
                      user.instagram_url.startsWith("http")
                        ? user.instagram_url
                        : `https://instagram.com/${user.instagram_url}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2.5 text-sm text-primary font-semibold hover:opacity-80 transition-opacity"
                  >
                    <Instagram className="w-5 h-5" />
                    <span>{extractSocialHandle(user.instagram_url)}</span>
                  </a>
                )}
                {user.facebook_url && (
                  <a
                    href={
                      user.facebook_url.startsWith("http")
                        ? user.facebook_url
                        : `https://facebook.com/${user.facebook_url}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2.5 text-sm text-primary font-semibold hover:opacity-80 transition-opacity"
                  >
                    <FacebookIcon className="w-5 h-5" />
                    <span>{extractSocialHandle(user.facebook_url)}</span>
                  </a>
                )}
              </div>

              {/* Tags & Intentions */}
              {allChips.length > 0 && (
                <div className="flex flex-wrap justify-start gap-2.5 w-full">
                  {allChips.map((chip, index) => (
                    <TagChip
                      key={index}
                      tag={chip}
                      tab="members"
                      className="px-2.5 py-2.5 text-primary border-primary rounded-lg text-[13px]"
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Show More / Show Less OR Empty State CTA */}
          {showEmptyState ? (
            <div className="w-full space-y-7.5 mt-5">
              <p className="text-sm leading-6.25 text-[#F2F0EB]/50 text-center">
                {t("profileHeader.emptyStateMessage") ||
                  "Tell the community about your spiritual journey, your practices, and what brings you to Rituality."}
              </p>
              <div className="grid grid-cols-2 gap-2.5">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-between items-center gap-3 px-3.75 py-3.25 h-auto bg-[#FFFFFF1A] rounded-[10px] text-[#F2F0EB] border-[#FFFFFF1A] text-sm font-semibold"
                  onClick={() => navigate("/profile/edit")}
                >
                  <span>
                    {t("profileHeader.emptyStateSteps.shareAboutYourself") ||
                      "Share about yourself"}
                  </span>
                  <ChevronRight className="w-6 h-6" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-between items-center gap-3 px-3.75 py-3.25 h-auto bg-[#FFFFFF1A] rounded-[10px] text-[#F2F0EB] border-[#FFFFFF1A] text-sm font-semibold"
                  onClick={() => navigate("/profile/edit#tab=traditions")}
                >
                  <span>
                    {t("profileHeader.emptyStateSteps.tellAboutSpiritualTraditions") ||
                      "Tell others about your Spiritual Traditions"}
                  </span>
                  <ChevronRight className="w-6 h-6" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-between items-center gap-3 px-3.75 py-3.25 h-auto bg-[#FFFFFF1A] rounded-[10px] text-[#F2F0EB] border-[#FFFFFF1A] text-sm font-semibold"
                  onClick={() => navigate("/profile/edit#tab=photos")}
                >
                  <span>
                    {t("profileHeader.emptyStateSteps.uploadProfilePhoto") ||
                      "Upload your profile photo"}
                  </span>
                  <ChevronRight className="w-6 h-6" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-between items-center gap-3 px-3.75 py-3.25 h-auto bg-[#FFFFFF1A] rounded-[10px] text-[#F2F0EB] border-[#FFFFFF1A] text-sm font-semibold"
                  onClick={() => navigate("/profile/edit#tab=traditions")}
                >
                  <span>
                    {t("profileHeader.emptyStateSteps.shareEthnicBackground") ||
                      "Share about your Ethnic Background"}
                  </span>
                  <ChevronRight className="w-6 h-6" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-between items-center gap-3 px-3.75 py-3.25 h-auto bg-[#FFFFFF1A] rounded-[10px] text-[#F2F0EB] border-[#FFFFFF1A] text-sm font-semibold"
                  onClick={() => navigate("/profile/edit#tab=photos")}
                >
                  <span>
                    {t("profileHeader.emptyStateSteps.uploadGalleryPhotos") ||
                      "Upload Gallery Photos"}
                  </span>
                  <ChevronRight className="w-6 h-6" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-between items-center gap-3 px-3.75 py-3.25 h-auto bg-[#FFFFFF1A] rounded-[10px] text-[#F2F0EB] border-[#FFFFFF1A] text-sm font-semibold"
                  onClick={() => navigate("/profile/edit#tab=intentions")}
                >
                  <span>
                    {t("profileHeader.emptyStateSteps.chooseIntentions") ||
                      "Choose intentions you'd like to focus on"}
                  </span>
                  <ChevronRight className="w-6 h-6" />
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="ghost"
              type="button"
              className="flex items-center gap-1.25 md:gap-1.5 py-1.25 px-2.5 h-7.5 text-sm text-primary font-semibold bg-[#F2F0EB1A] md:bg-transparent  rounded-full hover:opacity-80 transition-opacity mt-7.5 md:mt-5 cursor-pointer"
              onClick={() => setDetailsExpanded(!detailsExpanded)}
            >
              <span>
                {detailsExpanded ? t("profileHeader.showLess") : t("profileHeader.showMore")}
              </span>
              <ChevronUp className={cn("w-4 h-4", !detailsExpanded && "rotate-180")} />
            </Button>
          )}
        </div>
      </div>

      <ShareModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        shareUrl={`/profile/${user.id}`}
        username={user.username}
        shareText="Check out this profile on Rituality!"
      />
    </>
  )
}
